import { makeAutoObservable, runInAction } from 'mobx'
import UserProfileService from './../../../shared/services/UserProfile.service'
import StorageService from './../../../shared/services/Storage.service'
import PushDeviceService from './../../../shared/services/PushDevice.service'
import PushMessageService from './../../../shared/services/PushMessage.service'

// Visible-truncation points, not hard limits. Most platforms clip a
// notification around here, so the counters warn well before the server caps.
export const TITLE_VISIBLE = 50
export const BODY_VISIBLE = 150
export const TITLE_MAX = 120
export const BODY_MAX = 400

// Mirrors cleanLink() in functions/handlers/rest/push-messages.js, which
// silently drops any link that fails it.
const LINK_PATH = /^\/[A-Za-z0-9\-._~/?#[\]@!$&'()*+,;=%]*$/

const uuid = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID()
    if (window.crypto && window.crypto.getRandomValues) {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0'))
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex
        .slice(8, 10)
        .join('')}-${hex.slice(10, 16).join('')}`
    }
  } catch (ex) {}
  return `f${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

/** Viewer's local timezone; asserts no timezone the API has not stated. */
const formatWhen = value => {
  if (!value) return ''
  let d = new Date(value)
  if (isNaN(d.getTime())) d = new Date(`${value}`.replace(' ', 'T'))
  if (isNaN(d.getTime())) return `${value}`
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const emptyForm = () => ({
  title: '',
  body: '',
  link_url: '',
  severity: 'standard',
  send_email: false,
})

/**
 * Compose + send state for the BST -> Agent message card on an agent's profile.
 *
 * Singleton: the profile page shows exactly one agent at a time, and init()
 * resets everything whenever the agent changes.
 */
class AgentPushMessageStore {
  constructor() {
    makeAutoObservable(this)
  }

  agentId = null
  form = emptyForm()
  devices = []
  history = []
  isLoadingDevices = false
  isLoadingHistory = false
  isConfirming = false
  isSending = false
  showValidity = false
  /** @type {null|{status:'success'|'error'|'mismatch', message:string, detail?:string}} */
  outcome = null

  // Claimed per composition. Regenerated after a successful send so the NEXT
  // message is a distinct idempotency key.
  clientSendId = uuid()

  init = agentId => {
    const id = parseInt(agentId)
    if (!id || isNaN(id)) return
    if (this.agentId === id) return

    runInAction(() => {
      this.agentId = id
      this.form = emptyForm()
      this.devices = []
      this.history = []
      this.outcome = null
      this.isConfirming = false
      this.showValidity = false
      this.clientSendId = uuid()
      this._emailTouched = false
    })

    this.loadDevices()
    this.loadHistory()
  }

  // Set once the admin touches the email toggle, so we stop overriding it.
  _emailTouched = false

  setField = (name, value) => {
    runInAction(() => {
      if (name === 'send_email') this._emailTouched = true
      this.form = { ...this.form, [name]: value }
      // Any edit invalidates a previous result — never leave a stale "sent"
      // banner above a form the admin has started changing.
      if (this.outcome) this.outcome = null
    })
  }

  loadDevices = async () => {
    if (!this.agentId) return
    runInAction(() => {
      this.isLoadingDevices = true
    })
    try {
      const result = await PushDeviceService.search({
        search: { user_id: this.agentId },
        order_by: { last_seen_at: 'DESC' },
        pagination: false,
      })
      const models = (result && result.models) || (Array.isArray(result) ? result : [])
      runInAction(() => {
        this.devices = models
        // Unless push is PROVEN to work for this agent, pre-select email rather
        // than letting an admin send into a void. Only until they say otherwise.
        if (!this._emailTouched && this.shouldFallbackToEmail) {
          this.form = { ...this.form, send_email: true }
        }
      })
    } catch (ex) {
      console.error('[push-admin] loadDevices failed', ex)
      runInAction(() => {
        this.devices = []
      })
    } finally {
      runInAction(() => {
        this.isLoadingDevices = false
      })
    }
  }

  loadHistory = async () => {
    if (!this.agentId) return
    runInAction(() => {
      this.isLoadingHistory = true
    })
    try {
      const result = await PushMessageService.search({
        search: { user_id: this.agentId },
        order_by: { created_at: 'DESC' },
        pagination: { per_page: 25 },
      })
      const models = (result && result.models) || (Array.isArray(result) ? result : [])
      runInAction(() => {
        this.history = models
      })
    } catch (ex) {
      console.error('[push-admin] loadHistory failed', ex)
      runInAction(() => {
        this.history = []
      })
    } finally {
      runInAction(() => {
        this.isLoadingHistory = false
      })
    }
  }

  /** Devices that could actually receive a push right now. */
  get liveDevices() {
    return (this.devices || []).filter(
      d =>
        !d.revoked_at &&
        `${d.permission_state}` === 'granted' &&
        !(`${d.platform}` === 'ios' && !parseInt(d.is_standalone))
    )
  }

  /** Devices that have PROVEN they display notifications, or that a real message reached. */
  get provenDevices() {
    return this.liveDevices.filter(d => d.verified_at || d.last_clicked_at)
  }

  /** Live devices where the agent reported that nothing appeared. */
  get failedDevices() {
    return this.liveDevices.filter(d => d.verify_failed_at && !d.verified_at)
  }

  /**
   * Delivery confidence, not just opt-in status.
   *
   * "Opted in" and "actually receives notifications" are different facts. If the
   * operating system is suppressing the browser, permission still reads granted
   * and the token still works — the two states are indistinguishable from the
   * server. Only the agent's own confirmation, or a click on a real message,
   * separates them.
   *
   * Every level carries a DATE where one exists: verification is point-in-time,
   * and someone can mute their OS the day after confirming.
   */
  get readiness() {
    const devices = this.devices || []
    if (this.isLoadingDevices) return { level: 'loading', label: 'Checking devices…' }
    if (!devices.length)
      return {
        level: 'unknown',
        label: 'Unknown — this agent has not opened the back office since notifications launched',
      }

    const live = this.liveDevices.length
    const proven = this.provenDevices
    const failed = this.failedDevices

    if (proven.length) {
      const latest = proven
        .map(d => d.last_clicked_at || d.verified_at)
        .filter(Boolean)
        .sort()
        .pop()
      return {
        level: 'verified',
        label: `Verified — ${proven.length} of ${live} device${live === 1 ? '' : 's'} confirmed`,
        detail: latest ? `last confirmed ${formatWhen(latest)}` : null,
      }
    }

    // The dangerous state: a live token whose owner told us nothing appears.
    // Without this it would read as healthy and BST would send into a void.
    if (failed.length && failed.length === live)
      return {
        level: 'test-failed',
        label: 'Enabled, but the agent reported notifications do not appear',
        detail: 'Likely blocked at the device/OS level — email is the reliable route',
      }

    if (live > 0)
      return {
        level: 'unverified',
        label: `Enabled on ${live} device${live === 1 ? '' : 's'} — not yet confirmed`,
        detail: 'Opted in, but never confirmed a test notification was visible',
      }

    if (
      devices.some(
        d => `${d.platform}` === 'ios' && !parseInt(d.is_standalone) && `${d.permission_state}` === 'granted'
      )
    )
      return {
        level: 'ios',
        label: 'Not added to Home Screen — iPhone cannot receive push yet',
      }

    if (devices.every(d => `${d.permission_state}` === 'denied'))
      return { level: 'blocked', label: 'Blocked in browser settings' }

    return { level: 'none', label: 'Never enabled on any device' }
  }

  /** True when at least one device holds a live token. */
  get canReceivePush() {
    return this.liveDevices.length > 0
  }

  /**
   * Should email be pre-selected? Keyed on CONFIDENCE, not mere opt-in.
   *
   * 'unverified' counts as a fallback case on purpose: the agent opted in but
   * has never confirmed anything is visible, so push may well be going nowhere.
   * Only a verified or click-proven device is trusted to stand on its own.
   */
  get shouldFallbackToEmail() {
    return this.readiness.level !== 'verified'
  }

  /**
   * Exactly where this message will land, given the agent's current devices and
   * the email toggle.
   *
   * The card stays usable when push is unavailable — the message still reaches
   * the agent via their Alerts page and, optionally, email. Hiding the form
   * would remove real capability during exactly the window when almost nobody
   * has enabled push yet. So instead we state the outcome plainly.
   */
  /**
   * One sentence fragment naming every channel this will actually use, e.g.
   * "their Alerts page, push (1 device) and email".
   *
   * Replaces the old three-row checklist: the admin needs the answer, not a
   * table to read. Alerts page is listed first because it is the only channel
   * that always works.
   */
  get deliverySummary() {
    const parts = ['their Alerts page']
    const live = this.liveDevices.length
    if (live) parts.push(`push (${live} device${live === 1 ? '' : 's'})`)
    if (this.form.send_email) parts.push('email')
    if (parts.length === 1) return parts[0]
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  }

  get deliveryPlan() {
    const live = this.liveDevices.length
    return [
      { label: 'Their Alerts page', active: true, detail: 'always' },
      {
        label: 'Push notification',
        active: live > 0,
        detail: live > 0 ? `${live} device${live === 1 ? '' : 's'}` : 'no enabled device',
      },
      {
        label: 'Email',
        active: !!this.form.send_email,
        detail: this.form.send_email ? 'will send' : 'off',
      },
    ]
  }

  get titleRemaining() {
    return TITLE_VISIBLE - (this.form.title || '').length
  }

  get bodyRemaining() {
    return BODY_VISIBLE - (this.form.body || '').length
  }

  /**
   * Links must be back-office PATHS, not absolute URLs.
   *
   * The service worker resolves "/leaderboards" against its own origin, so one
   * message works on localhost, beta and prod alike. An absolute URL hard-codes
   * an environment — a localhost link reaches every recipient dead, and a beta
   * link walks prod users out of their own environment.
   *
   * Rather than refuse a pasted URL, strip our own origin off it. Refusing is
   * only correct for a genuinely foreign host.
   */
  normaliseLink = value => {
    const raw = `${value || ''}`.trim()
    if (!raw) return { value: '', error: null }

    if (raw.startsWith('/')) {
      if (raw.startsWith('//')) return { value: raw, error: 'Enter a path such as /leaderboards.' }
      if (!LINK_PATH.test(raw)) return { value: raw, error: 'Links cannot contain spaces or special characters.' }
      return { value: raw, error: null }
    }

    if (/^https?:\/\//i.test(raw)) {
      try {
        const url = new URL(raw)
        // Any host we plausibly are. Same-origin is the common paste; the usabg
        // hosts cover an admin copying a link out of beta or prod.
        const ours = url.origin === window.location.origin || /(^|\.)usabg\.com$/i.test(url.hostname)
        if (ours)
          return {
            value: `${url.pathname}${url.search}${url.hash}`,
            error: null,
          }
        return {
          value: raw,
          error: 'Links must point to a page inside the back office.',
        }
      } catch (ex) {
        return { value: raw, error: 'That does not look like a valid link.' }
      }
    }

    return {
      value: raw,
      error: 'Enter a path starting with / — for example /leaderboards.',
    }
  }

  /** Validation message for the link field, or null. */
  get linkError() {
    return this.normaliseLink(this.form.link_url).error
  }

  /** Called on blur: rewrite a pasted absolute URL into a path in place. */
  normaliseLinkField = () => {
    const { value, error } = this.normaliseLink(this.form.link_url)
    if (!error && value !== this.form.link_url) {
      runInAction(() => {
        this.form = { ...this.form, link_url: value }
      })
    }
  }

  /** The server strips < and >, so what was confirmed would not be what is sent. */
  get markupError() {
    return /[<>]/.test(`${this.form.title || ''}${this.form.body || ''}`)
      ? 'Remove < and > — messages are plain text.'
      : null
  }

  get isValid() {
    const title = (this.form.title || '').trim()
    const body = (this.form.body || '').trim()
    if (!title || title.length > TITLE_MAX) return false
    if (!body || body.length > BODY_MAX) return false
    if (this.linkError || this.markupError) return false
    return true
  }

  /**
   * Why the form will not advance, in the admin's words. Without this the
   * button simply did nothing and there was no way to find out why.
   */
  get blockedReason() {
    if (!(this.form.title || '').trim()) return 'Add a title.'
    if (!(this.form.body || '').trim()) return 'Add a message.'
    if ((this.form.title || '').trim().length > TITLE_MAX) return `The title is over the ${TITLE_MAX} character limit.`
    if ((this.form.body || '').trim().length > BODY_MAX) return `The message is over the ${BODY_MAX} character limit.`
    return this.markupError || this.linkError
  }

  openConfirm = () => {
    // Normalise first: a pasted absolute URL should become a path and proceed,
    // not be rejected.
    this.normaliseLinkField()

    if (!this.isValid) {
      runInAction(() => {
        this.showValidity = true
      })
      return
    }
    runInAction(() => {
      this.showValidity = false
      this.isConfirming = true
    })
  }

  cancelConfirm = () => {
    runInAction(() => {
      this.isConfirming = false
    })
  }

  /** Send to the agent whose profile is open. */
  send = async () => this._dispatch(this.agentId, false)

  /**
   * Send the composed message to the admin themselves. Uses the REAL admin id
   * (ignoreAssumed) because reaching this screen means they are impersonating
   * the agent — getUserId() would return the agent and "test" would become a
   * live send to the very person we are trying to avoid surprising.
   */
  sendTestToSelf = async () => {
    const selfId = UserProfileService.getUserId(true)
    return this._dispatch(selfId, true)
  }

  _dispatch = async (recipientId, isTest) => {
    if (!this.isValid) {
      runInAction(() => {
        this.showValidity = true
      })
      return
    }
    if (this.isSending) return

    runInAction(() => {
      this.isSending = true
      this.outcome = null
    })

    // A test must never consume the real message's idempotency key, or the
    // subsequent live send would be swallowed as a duplicate.
    const sendId = isTest ? uuid() : this.clientSendId

    try {
      const result = await PushMessageService.send({
        user_id: recipientId,
        client_send_id: sendId,
        title: this.form.title.trim(),
        body: this.form.body.trim(),
        link_url: (this.form.link_url || '').trim() || null,
        severity: this.form.severity,
        send_email: !!this.form.send_email,
        // Assuming an agent signs us in AS them, so the token identifies the
        // recipient. This is the handle the server uses to look up who is
        // really behind the session (user_assumes.parent_user_id) and record
        // that as sent_by. Not an identity claim — the server binds the lookup
        // to the caller, so a forged value matches no row.
        assume_id: StorageService.get('assume_id') || null,
      })

      if (!result || !result.id) throw new Error('The server did not confirm the send.')

      // Constraint 2, L7: trust the SERVER's account of who it sent to, not the
      // id we asked for. A mismatch is loud, never a success message.
      const echoed = result.recipient && parseInt(result.recipient.id)
      if (echoed && parseInt(recipientId) !== echoed) {
        runInAction(() => {
          this.outcome = {
            status: 'mismatch',
            message: 'The server reported a different recipient than expected. Do not resend — report this.',
            detail: `Requested #${recipientId}, server reported #${echoed}.`,
          }
        })
        return
      }

      const who = (result.recipient && result.recipient.name) || `#${recipientId}`
      const delivered = parseInt(result.devices_delivered || 0)
      const targeted = parseInt(result.devices_targeted || 0)

      runInAction(() => {
        this.outcome = {
          status: 'success',
          message: result.duplicate
            ? `Already sent to ${who}. Nothing was sent twice.`
            : isTest
            ? `Test sent to you (${who}).`
            : `Sent to ${who}.`,
          detail: [
            targeted
              ? `${delivered} of ${targeted} device${targeted === 1 ? '' : 's'} reached`
              : 'No push-enabled devices — delivered to their Alerts page',
            parseInt(result.emailed) ? 'email sent' : null,
          ]
            .filter(Boolean)
            .join(' · '),
        }
        if (!isTest) {
          this.form = emptyForm()
          this.clientSendId = uuid()
          this.isConfirming = false
        }
      })

      if (!isTest || `${recipientId}` === `${this.agentId}`) this.loadHistory()
      this.loadDevices()
    } catch (ex) {
      console.error('[push-admin] send failed', ex)
      runInAction(() => {
        this.outcome = {
          status: 'error',
          message: (ex && ex.message) || 'The message could not be sent.',
        }
        this.isConfirming = false
      })
    } finally {
      runInAction(() => {
        this.isSending = false
      })
    }
  }
}

export default new AgentPushMessageStore()
